import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLatest } from 'ahooks';
import { useSelector } from 'react-redux';
import { selectCurrentServingOrder } from '@/store/service';
import { ImmersionValueContext } from '../index';
import MassageHeadUI from '../massage-head-ui';
import { BODY_BG, VIDEO_URLS } from './config';

import cx from './index.module.scss';

interface Props {
  show: boolean;
  massageHeadRealtimeStatus: MassageHeadRealtimeStatus;
}

export interface RealTimeModeRefAttrs {
  playVideo: (onPlay?: VoidFunc) => void;
}

const ModelBoundaryCoord = [
  [500, 140],
  [780, 640],
];

const RealTimeMode = forwardRef<RealTimeModeRefAttrs, Props>(({ show, massageHeadRealtimeStatus }, ref) => {
  const awesomePointVideoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlay, setIsVideoPlay] = useState(false);
  const { massageState } = useContext(ImmersionValueContext);

  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [bodyBgUrl, setBodyBgUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const latestIsVideoPlay = useLatest(isVideoPlay);
  const { model_position = [] } = massageHeadRealtimeStatus || {};

  const formatCoord = useMemo(() => {
    if (!Array.isArray(model_position)) {
      return {};
    }

    const [x = 0, y = 0] = model_position || [];

    /** 模型腰比较细，在极端情况将图形渲染在人体内 */
    let offsetX = 0;

    if (x >= 0.92) {
      offsetX = y >= 0.6 && y <= 0.92 ? 25 : 0;
    }

    if (x <= 0.08) {
      offsetX = y >= 0.6 && y <= 0.92 ? -25 : 0;
    }

    return {
      left: `${(ModelBoundaryCoord[1][0] - ModelBoundaryCoord[0][0]) * x + ModelBoundaryCoord[0][0] - offsetX}px`,
      top: `${(ModelBoundaryCoord[1][1] - ModelBoundaryCoord[0][1]) * y + ModelBoundaryCoord[0][1]}px`,
    };
  }, [model_position]);

  const init = useCallback(() => {
    // 统一事件处理器
    const handleVideoEvents = event => {
      switch (event.type) {
        case 'play':
          setTimeout(() => setIsVideoPlay(true), 300);
          console.log('play');
          break;
        case 'ended':
          setIsVideoPlay(false);
          console.log('play ended');
          break;
      }
    };

    // 批量绑定事件
    ['play', 'ended', 'pause'].forEach(eventType => {
      awesomePointVideoRef.current?.addEventListener(eventType, handleVideoEvents);
    });

    return () => {
      if (awesomePointVideoRef.current) {
        awesomePointVideoRef.current.pause();
        awesomePointVideoRef.current.currentTime = 0;
        setIsVideoPlay(false);
        ['play', 'ended', 'pause'].forEach(eventType => {
          awesomePointVideoRef.current.removeEventListener(eventType, handleVideoEvents);
        });
      }
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      playVideo: (onPlay?: VoidFunc) => {
        if (latestIsVideoPlay.current) return;

        onPlay?.();
        awesomePointVideoRef.current?.play();
      },
    }),
    [latestIsVideoPlay]
  );

  useEffect(() => {
    if (show) {
      return init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (currentServingOrder?.orderId) {
      const isAdbomenPlan = currentServingOrder?.bodyPart === 'Abdomen';
      setBodyBgUrl(BODY_BG[currentServingOrder?.customerGender]?.[isAdbomenPlan ? 'front' : 'back']);
      setVideoUrl(
        VIDEO_URLS[currentServingOrder?.productItems?.[0]]?.[currentServingOrder?.customerGender]?.[
          isAdbomenPlan ? 'front' : 'back'
        ]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId]);

  return (
    <div className={cx('RealTimeMode', { show })}>
      <div className={cx('real-time-mode-box')}>
        <div className={cx('back-blue')} />
        <div className={cx('video-box', { show: isVideoPlay })}>
          <video ref={awesomePointVideoRef} src={videoUrl} muted />
        </div>
        <div className={cx('body-bg')} style={{ backgroundImage: `url(${bodyBgUrl})` }} />
        <div className={cx('circle-bg', { 'no-animate': massageState !== 'Running_Massaging' })} />
        <div className={cx('massage-head')} style={formatCoord}>
          <MassageHeadUI massageHeadRealtimeStatus={massageHeadRealtimeStatus} />
        </div>
      </div>
    </div>
  );
});

export default RealTimeMode;
